import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Tab,
  TabList,
  Tabs,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { MrFilterSetting } from "../../../api/settings";
import { MappedConsolidatedResources } from "../shared-logic/consolidated-context/reducer";
import { useRefAndUpdateHeight } from "../shared-logic/elementHeight";
import { METRIPORT_PRIMARY } from "../shared-logic/style";
import { getAiSummaryFromMappedConsolidated } from "./fhir-sections/summaries/content";
import { useFhirSections } from "../shared-logic/useFhirSections";
import { FhirSections } from "./fhir-sections";

const MAX_TABS_SHOWN = 8;

type TabOption = {
  index: number;
  display: string;
  id: string;
};

export function PatientTabs({
  filters,
  mappedConsolidated,
  headerHeight = 0,
  isIFrame = false,
  isLimited = false,
  isLoadingData = false,
}: {
  filters?: MrFilterSetting[];
  mappedConsolidated?: MappedConsolidatedResources | undefined;
  headerHeight?: number;
  isIFrame?: boolean;
  isLimited?: boolean;
  isLoadingData: boolean;
}) {
  const [tabsHeight, setTabsHeight] = useState<number>(0);
  const [index, setIndex] = useState(0);
  const [isManualScroll, setIsManualScroll] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { filteredFhirSections } = useFhirSections();

  const tabsBgColor = useColorModeValue("gray.100", "gray.900");
  const tabTextColor = useColorModeValue("black", "white");
  const { elementRef, updateHeight } = useRefAndUpdateHeight(setTabsHeight);

  useEffect(() => {
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const aiSummary = getAiSummaryFromMappedConsolidated(mappedConsolidated);

  const tabOptions = generateTabOptions(filteredFhirSections, filters, aiSummary);
  const maxTabsShown =
    useBreakpointValue(
      {
        base: 4,
        md: isLimited ? 5 : 3,
        lg: isLimited ? 7 : 5,
        xl: isLimited ? MAX_TABS_SHOWN : 7,
        "2xl": MAX_TABS_SHOWN,
      },
      { ssr: false }
    ) ?? 0;

  const totalOffset = tabsHeight + headerHeight;

  useEffect(() => {
    const onScroll = () => {
      if (isManualScroll) {
        return;
      }
      const index = setIndexIfInFrame(window.scrollY, tabOptions, totalOffset);

      if (index === undefined) {
        return;
      }

      if (index >= maxTabsShown) {
        setIndex(maxTabsShown);
        return;
      }

      setIndex(index);
    };

    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("wheel", () => setIsManualScroll(false), { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("wheel", () => setIsManualScroll(false));
    };
  }, [isManualScroll, tabOptions, maxTabsShown, totalOffset]);

  return (
    <Skeleton isLoaded={!isLoadingData} borderRadius={5} my={2} height="50px">
      <Tabs
        ref={elementRef}
        index={index}
        onChange={index => {
          if (index < maxTabsShown) {
            setIsManualScroll(true);
            scrollToIndex(index, tabOptions, totalOffset, isIFrame);
            setIndex(index);
          }
        }}
        py={3}
        size={isLimited ? "xs" : "md"}
        variant="soft-rounded"
        bg={tabsBgColor}
      >
        <TabList overflowX={"scroll"} justifyContent={maxTabsShown > 0 ? "left" : "right"}>
          {tabOptions
            .filter(tab => tab.index < maxTabsShown)
            .map((tab, i) => {
              return (
                <Tab
                  color={tabTextColor}
                  _selected={{ color: "white", bg: METRIPORT_PRIMARY }}
                  whiteSpace={"nowrap"}
                  key={tab.index}
                  cursor={"pointer"}
                  py={isLimited ? 0 : 4}
                  px={isLimited ? 4 : 5}
                  mr={4}
                  ml={i === 0 ? 0 : 4}
                  fontSize={isLimited ? "xs" : "md"}
                  height={isLimited ? "auto" : "40px"}
                >
                  {tab.display}
                </Tab>
              );
            })}
          <Tab height={isLimited ? "auto" : "40px"} _selected={{ bg: METRIPORT_PRIMARY }}>
            <Menu placement={"bottom"} isOpen={isOpen}>
              <MenuButton
                fontWeight={"normal"}
                bg={"transparent"}
                color={index >= maxTabsShown ? "white" : undefined}
                _active={{ bg: "transparent" }}
                _hover={{ bg: "transparent" }}
                onMouseEnter={onOpen}
                onMouseLeave={onClose}
                as={Button}
                size={isLimited ? "xs" : "md"}
              >
                Other
              </MenuButton>
              <MenuList onMouseEnter={onOpen} onMouseLeave={onClose}>
                {tabOptions
                  .filter(tab => tab.index >= maxTabsShown)
                  .map((tab, i) => {
                    return (
                      <MenuItem
                        color={tabTextColor}
                        key={i}
                        onClick={event => {
                          if (isIFrame) {
                            event.stopPropagation();
                          }
                          setIsManualScroll(true);
                          scrollToIndex(tab.index, tabOptions, totalOffset, isIFrame);
                          setIndex(maxTabsShown);
                          onClose();
                        }}
                        fontSize={isLimited ? "xs" : "md"}
                      >
                        {tab.display}
                      </MenuItem>
                    );
                  })}
              </MenuList>
            </Menu>
          </Tab>
        </TabList>
      </Tabs>
    </Skeleton>
  );
}

function generateTabOptions(
  filteredFhirSections: FhirSections,
  mrFilterSettings?: MrFilterSetting[],
  aiSummary?: string
): TabOption[] {
  const tabOptions: TabOption[] = [];

  if (aiSummary) {
    tabOptions.push({ index: 0, id: "ai-summary", display: "AI Summary" });
  }

  if (mrFilterSettings) {
    mrFilterSettings.forEach((filter, i) => {
      const display = filteredFhirSections.find(section => section.key === filter.key)?.title;
      if (display) {
        tabOptions.push({
          index: aiSummary ? i + 1 : i,
          id: filter.key,
          display,
        });
      }
    });
  } else {
    filteredFhirSections.forEach((section, i) => {
      tabOptions.push({
        index: aiSummary ? i + 1 : i,
        id: section.key,
        display: section.title,
      });
    });
  }

  return tabOptions;
}

function setIndexIfInFrame(
  scrollOffset: number,
  tabOptions: { display: string; id: string }[],
  totalOffset: number
): number | undefined {
  const currentOffset = scrollOffset + totalOffset;

  for (let i = 0; i < tabOptions.length; i++) {
    const tab = tabOptions[i];
    if (!tab) continue;
    const element = document.getElementById(tab.id);
    if (!element) {
      continue;
    }

    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - totalOffset;

    if (currentOffset >= offsetPosition && currentOffset < offsetPosition + element.clientHeight) {
      return i;
    }
  }
  return undefined;
}

function scrollToIndex(
  index: number,
  tabOptions: { display: string; id: string }[],
  totalOffset: number,
  isIFrame = false
) {
  const tab = tabOptions[index];
  if (!tab) return;
  const element = document.getElementById(tab.id);
  if (!element) {
    return;
  }

  const elementPosition = element.getBoundingClientRect().top;
  const padding = 5;
  const offsetPosition = elementPosition + window.pageYOffset - totalOffset - padding;

  window.scrollTo({
    top: offsetPosition,
    behavior: isIFrame ? undefined : "smooth",
  });
}
